// 화면 그리기(§9.8) — 편집기: 도구별 마우스 동작 → 문서 연산 · 되돌리기(자기 것만) · 선택 · 레이저 꼬리.
//
// DOM 을 모른다 — 좌표는 호출측이 비율 좌표(0~1)와 영역 크기(px)로 넘긴다(뷰어 캔버스·원격지 그림판 창이 같은 편집기를 쓴다).
// 연산은 **먼저 내 문서에 적용하고 → 적용된 것만 `send` 로 내보낸다**(상대 문서가 거절할 연산을 흘리지 않는다).
// 상대가 보낸 연산은 `applyRemote` — 되돌리기 기록에 넣지 않는다(되돌리기는 자기 것만, §9.8.3).
import { DrawDoc, LIMITS, cloneObj, q } from './model.ts';
import type { Author, DrawObj, Layer, Op, ShapeObj, StrokeObj, TextObj } from './model.ts';
import { DUPLICATE_OFFSET_PX, hitObj, keepPoint, movePatch } from './geometry.ts';
import type { Pt, Space } from './geometry.ts';
import {
  DEFAULT_STYLE,
  FILLABLE_TOOLS,
  HISTORY_MAX,
  HIT_TOLERANCE_PX,
  LASER_FADE_MS,
  MARKER_WIDTH_SCALE,
  SHAPE_MIN_PX,
  SHAPE_TOOLS,
  STROKE_MIN_STEP_PX,
  STROKE_TOOLS,
  TEXT_SIZES,
} from './style.ts';
import type { Tool } from './style.ts';

/** 되돌리기 한 단위 — 적용할 연산 묶음(순서대로). */
interface Action {
  undo: Op[];
  redo: Op[];
}

/** 레이저 꼬리의 한 점(`t` = 받은 시각 ms). */
export interface LaserPoint {
  x: number;
  y: number;
  t: number;
}

export interface EditorHooks {
  /** 문서·초안·선택·레이저가 바뀌었다(화면이 다시 그린다). */
  onChange?: () => void;
  /** 상대에게 보낼 연산(D2 에서 채널에 연결). */
  send?: (op: Op) => void;
  /** 상대에게 보낼 레이저 점(저장하지 않는 일시 표시). */
  sendLaser?: (p: Pt) => void;
  /** 시각(ms) — 시험에서 바꿔 끼운다. */
  now?: () => number;
}

/** 끌기 상태 — 누른 순간 정해진다. */
type Gesture =
  | { kind: 'stroke'; id: string; last: Pt }
  | { kind: 'shape'; start: Pt }
  | { kind: 'erase'; removed: DrawObj[] }
  | { kind: 'move'; start: Pt; origin: DrawObj[]; moved: boolean }
  | { kind: 'laser' }
  | null;

export class DrawEditor {
  readonly doc = new DrawDoc();
  tool: Tool = 'pen';
  color: string = DEFAULT_STYLE.color;
  width: number = DEFAULT_STYLE.width;
  fill: boolean = DEFAULT_STYLE.fill;
  /** 끄는 중인 도형 미리보기(문서에 아직 없다). */
  draft: ShapeObj | null = null;
  /** 선택된 객체 id. */
  readonly selection = new Set<string>();
  /** 작성자별 레이저 꼬리. */
  readonly lasers = new Map<Author, LaserPoint[]>();

  private seq = 0;
  private past: Action[] = [];
  private future: Action[] = [];
  private gesture: Gesture = null;

  readonly author: Author;
  private hooks: EditorHooks;

  // ⚠️ 매개변수 속성(`constructor(readonly author …)`)을 쓰지 않는다 — Node 의 타입 제거 실행(`node --test`)이 받지 않는다.
  constructor(author: Author, hooks: EditorHooks = {}) {
    this.author = author;
    this.hooks = hooks;
  }

  // ── 설정 ────────────────────────────────────────────────

  setTool(t: Tool): void {
    this.cancelGesture();
    this.tool = t;
    if (t !== 'select') this.selection.clear();
    this.changed();
  }

  /** 색 — 선택된 것이 있으면 그것들의 색도 바꾼다(한 번에 되돌릴 수 있게 한 단위). */
  setColor(color: string): void {
    this.color = color;
    this.patchSelection((o) => (o.color === color ? null : { color }));
    this.changed();
  }

  setWidth(width: number): void {
    this.width = width;
    this.patchSelection((o) => {
      const w = this.widthFor(o.kind);
      return o.width === w ? null : { width: w };
    });
    this.changed();
  }

  setFill(fill: boolean): void {
    this.fill = fill;
    this.patchSelection((o) =>
      (o.kind === 'rect' || o.kind === 'ellipse') && !!o.fill !== fill ? { fill } : null,
    );
    this.changed();
  }

  /** 채우기 단추가 뜻이 있는가 — 채울 수 있는 도형 도구이거나, 선택한 것 중에 사각형·원이 있다(뷰어·원격지 막대 공용). */
  get fillable(): boolean {
    return (
      FILLABLE_TOOLS.has(this.tool) ||
      [...this.selection].some((id) => {
        const k = this.doc.get(id)?.kind;
        return k === 'rect' || k === 'ellipse';
      })
    );
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  // ── 마우스 ──────────────────────────────────────────────

  down(p: Pt, s: Space): void {
    this.cancelGesture();
    const layer = this.layer;
    if (STROKE_TOOLS.has(this.tool)) {
      if (this.doc.size >= LIMITS.maxObjects) return;
      const obj: StrokeObj = {
        id: this.nextId(),
        by: this.author,
        layer,
        kind: this.tool as StrokeObj['kind'],
        color: this.color,
        width: this.widthFor(this.tool),
        pts: [q(p[0]), q(p[1])],
      };
      this.local({ t: 'begin', obj });
      this.gesture = { kind: 'stroke', id: obj.id, last: p };
    } else if (SHAPE_TOOLS.has(this.tool)) {
      this.gesture = { kind: 'shape', start: p };
      this.draft = this.makeShape(p, p);
    } else if (this.tool === 'eraser') {
      this.gesture = { kind: 'erase', removed: [] };
      this.eraseAt(p, s);
    } else if (this.tool === 'select') {
      const hit = this.topHit(p, s);
      if (!hit) {
        this.selection.clear();
      } else if (!this.selection.has(hit.id)) {
        this.selection.clear();
        this.selection.add(hit.id);
      }
      const origin = [...this.selection].map((id) => this.doc.get(id)).filter((o): o is DrawObj => !!o).map(cloneObj);
      this.gesture = origin.length ? { kind: 'move', start: p, origin, moved: false } : null;
    } else if (this.tool === 'laser') {
      this.gesture = { kind: 'laser' };
      this.laserPoint(this.author, p);
      this.hooks.sendLaser?.(p);
    }
    this.changed();
  }

  move(p: Pt, s: Space): void {
    const g = this.gesture;
    if (!g) return;
    switch (g.kind) {
      case 'stroke':
        if (keepPoint(g.last, p, s, STROKE_MIN_STEP_PX)) {
          g.last = p;
          this.local({ t: 'pts', id: g.id, p: [q(p[0]), q(p[1])] });
        }
        break;
      case 'shape':
        this.draft = this.makeShape(g.start, p);
        break;
      case 'erase':
        this.eraseAt(p, s);
        break;
      case 'move': {
        const dx = p[0] - g.start[0];
        const dy = p[1] - g.start[1];
        for (const o of g.origin) this.local({ t: 'upd', id: o.id, patch: movePatch(o, dx, dy, s) });
        g.moved = true;
        break;
      }
      case 'laser':
        this.laserPoint(this.author, p);
        this.hooks.sendLaser?.(p);
        break;
    }
    this.changed();
  }

  up(p: Pt, s: Space): void {
    const g = this.gesture;
    this.gesture = null;
    if (!g) return;
    switch (g.kind) {
      case 'stroke': {
        const inv = this.local({ t: 'end', id: g.id });
        const obj = this.doc.get(g.id);
        if (inv && obj) this.record({ undo: [inv], redo: [{ t: 'add', objs: [cloneObj(obj)] }] });
        break;
      }
      case 'shape': {
        const obj = this.makeShape(g.start, p);
        this.draft = null;
        const dist = Math.hypot((obj.b[0] - obj.a[0]) * s.w, (obj.b[1] - obj.a[1]) * s.h);
        if (dist >= SHAPE_MIN_PX) {
          obj.id = this.nextId();
          this.commit([{ t: 'add', objs: [obj] }]);
        }
        break;
      }
      case 'erase':
        if (g.removed.length) {
          this.record({ undo: [{ t: 'add', objs: g.removed }], redo: [{ t: 'del', ids: g.removed.map((o) => o.id) }] });
        }
        break;
      case 'move':
        if (g.moved) {
          const redo: Op[] = [];
          const undo: Op[] = [];
          for (const o of g.origin) {
            const now = this.doc.get(o.id);
            if (!now) continue; // 옮기는 사이 상대가 지웠다
            undo.push({ t: 'upd', id: o.id, patch: movePatch(o, 0, 0, s) });
            redo.push({ t: 'upd', id: o.id, patch: movePatch(now, 0, 0, s) });
          }
          if (undo.length) this.record({ undo, redo });
        }
        break;
      case 'laser':
        break;
    }
    this.changed();
  }

  // ── 명령 ────────────────────────────────────────────────

  /** 글자 추가(글자 도구 입력칸이 확정될 때). 빈 글자는 만들지 않는다. */
  addText(at: Pt, text: string): void {
    const t = text.replace(/\r\n?/g, '\n').trimEnd();
    if (!t) return;
    const obj: TextObj = {
      id: this.nextId(),
      by: this.author,
      layer: this.layer,
      kind: 'text',
      color: this.color,
      width: TEXT_SIZES[this.width] ?? TEXT_SIZES[6],
      at: [q(at[0]), q(at[1])],
      text: [...t].slice(0, LIMITS.maxTextChars).join(''),
    };
    this.commit([{ t: 'add', objs: [obj] }]);
    this.changed();
  }

  /** 지금 보이는 층 전체 지우기(양쪽 모두의 그림 — 되돌릴 수 있다). */
  clear(): void {
    this.selection.clear();
    this.commit([{ t: 'clear', layer: this.layer }]);
    this.changed();
  }

  /** 흰 판 켜기·끄기 — 되돌리기 대상이 아니다(보기 전환). */
  setBoard(on: boolean): void {
    this.cancelGesture();
    this.selection.clear();
    this.local({ t: 'board', on });
    this.changed();
  }

  deleteSelection(): void {
    if (!this.selection.size) return;
    this.commit([{ t: 'del', ids: [...this.selection] }]);
    this.selection.clear();
    this.changed();
  }

  /** 선택한 것을 조금 비켜 복제하고, 복제본을 선택한다. */
  duplicateSelection(s: Space): void {
    const src = [...this.selection].map((id) => this.doc.get(id)).filter((o): o is DrawObj => !!o);
    if (!src.length) return;
    const dx = DUPLICATE_OFFSET_PX / s.w;
    const dy = DUPLICATE_OFFSET_PX / s.h;
    const copies = src.map((o) => {
      const c = { ...cloneObj(o), ...movePatch(o, dx, dy, s), id: this.nextId(), by: this.author } as DrawObj;
      return c;
    });
    this.commit([{ t: 'add', objs: copies }]);
    this.selection.clear();
    for (const c of copies) this.selection.add(c.id);
    this.changed();
  }

  undo(): void {
    this.cancelGesture();
    const a = this.past.pop();
    if (!a) return;
    this.replay(a.undo);
    this.future.push(a);
    this.dropMissingSelection();
    this.changed();
  }

  redo(): void {
    this.cancelGesture();
    const a = this.future.pop();
    if (!a) return;
    this.replay(a.redo);
    this.past.push(a);
    this.dropMissingSelection();
    this.changed();
  }

  // ── 상대 ────────────────────────────────────────────────

  /** 상대가 보낸 연산 — 되돌리기 기록에 넣지 않는다. 적용했는지 돌려준다. */
  applyRemote(op: Op): boolean {
    // 적용 여부는 판번호로 본다 — `apply` 의 반환(역연산)은 획 도중(`begin`·`pts`)에 적용돼도 null 이다.
    const before = this.doc.rev;
    this.doc.apply(op);
    const ok = this.doc.rev !== before;
    this.dropMissingSelection();
    this.changed();
    return ok;
  }

  /** 상대의 레이저 점. */
  remoteLaser(by: Author, p: Pt): void {
    this.laserPoint(by, p);
    this.changed();
  }

  /** 전체 상태 받기(`sync`) — 내 기록은 무의미해지므로 비운다. 내 id 번호는 이어서 쓴다(겹침 방지). */
  loadSync(snap: { objs: DrawObj[]; board: boolean }): void {
    this.cancelGesture();
    this.doc.load(snap);
    for (const o of this.doc.all()) {
      if (o.by === this.author) this.seq = Math.max(this.seq, Number(o.id.slice(1)) || 0);
    }
    this.past = [];
    this.future = [];
    this.selection.clear();
    this.changed();
  }

  /** 그리기 끄기 — 모두 비운다. */
  reset(): void {
    this.cancelGesture();
    this.doc.reset();
    this.past = [];
    this.future = [];
    this.selection.clear();
    this.lasers.clear();
    this.changed();
  }

  /** 레이저 꼬리에서 사라질 때가 지난 점을 버린다 → 남은 점이 있으면 true(화면이 계속 다시 그린다). */
  pruneLasers(): boolean {
    const now = this.now();
    let alive = false;
    for (const [by, pts] of this.lasers) {
      const keep = pts.filter((l) => now - l.t < LASER_FADE_MS);
      if (keep.length) {
        this.lasers.set(by, keep);
        alive = true;
      } else {
        this.lasers.delete(by);
      }
    }
    return alive;
  }

  /** 끄는 중인가(화면이 커서 모양·포인터 캡처를 정한다). */
  get busy(): boolean {
    return this.gesture !== null;
  }

  // ── 내부 ────────────────────────────────────────────────

  private get layer(): Layer {
    return this.doc.board ? 'board' : 'screen';
  }

  private now(): number {
    return this.hooks.now ? this.hooks.now() : Date.now();
  }

  private nextId(): string {
    this.seq += 1;
    return `${this.author}${this.seq}`;
  }

  private widthFor(kind: string): number {
    return kind === 'marker' ? this.width * MARKER_WIDTH_SCALE : kind === 'text' ? TEXT_SIZES[this.width] ?? TEXT_SIZES[6] : this.width;
  }

  private makeShape(a: Pt, b: Pt): ShapeObj {
    return {
      id: 'v0', // 확정할 때 새 번호로 바꾼다(초안은 문서에 들어가지 않는다)
      by: this.author,
      layer: this.layer,
      kind: this.tool as ShapeObj['kind'],
      color: this.color,
      width: this.width,
      a: [q(a[0]), q(a[1])],
      b: [q(b[0]), q(b[1])],
      ...(FILLABLE_TOOLS.has(this.tool) ? { fill: this.fill } : {}),
    };
  }

  /** 내 문서에 적용하고, 적용됐으면 상대에게 보낸다 → 역연산. */
  private local(op: Op): Op | null {
    const before = this.doc.rev;
    const inv = this.doc.apply(op);
    if (this.doc.rev !== before) this.hooks.send?.(op);
    return inv;
  }

  /** 연산 묶음을 적용하고 한 단위로 기록한다(역연산은 거꾸로 적용해야 맞다). */
  private commit(ops: Op[]): void {
    const undo: Op[] = [];
    const redo: Op[] = [];
    for (const op of ops) {
      const inv = this.local(op);
      if (inv) {
        undo.unshift(inv);
        redo.push(op);
      }
    }
    if (redo.length) this.record({ undo, redo });
  }

  private record(a: Action): void {
    this.past.push(a);
    if (this.past.length > HISTORY_MAX) this.past.shift();
    this.future = [];
  }

  /** 되돌리기·다시 실행 — 상대가 그 사이 지운 것 등 적용되지 않는 연산은 건너뛴다. */
  private replay(ops: Op[]): void {
    for (const op of ops) this.local(op);
  }

  private patchSelection(make: (o: DrawObj) => Partial<DrawObj> | null): void {
    const ops: Op[] = [];
    for (const id of this.selection) {
      const o = this.doc.get(id);
      const patch = o && make(o);
      if (patch) ops.push({ t: 'upd', id, patch: patch as never });
    }
    if (ops.length) this.commit(ops);
  }

  private eraseAt(p: Pt, s: Space): void {
    if (this.gesture?.kind !== 'erase') return;
    const hits = this.doc.visible().filter((o) => hitObj(o, p, s, HIT_TOLERANCE_PX));
    if (!hits.length) return;
    const snap = hits.map(cloneObj);
    if (this.local({ t: 'del', ids: hits.map((o) => o.id) })) this.gesture.removed.push(...snap);
  }

  /** 맨 위(나중에 그린) 것부터 찾는다 — 겹쳐 있으면 보이는 것을 고른다. */
  private topHit(p: Pt, s: Space): DrawObj | undefined {
    const vis = this.doc.visible();
    for (let i = vis.length - 1; i >= 0; i--) {
      if (hitObj(vis[i], p, s, HIT_TOLERANCE_PX)) return vis[i];
    }
    return undefined;
  }

  private laserPoint(by: Author, p: Pt): void {
    const pts = this.lasers.get(by) ?? [];
    pts.push({ x: p[0], y: p[1], t: this.now() });
    this.lasers.set(by, pts);
  }

  /** 끄던 동작을 버린다(도구 바꾸기·되돌리기 등). 그리던 획은 거기까지로 끝낸다. */
  private cancelGesture(): void {
    const g = this.gesture;
    this.gesture = null;
    this.draft = null;
    if (g?.kind === 'stroke') {
      const inv = this.local({ t: 'end', id: g.id });
      const obj = this.doc.get(g.id);
      if (inv && obj) this.record({ undo: [inv], redo: [{ t: 'add', objs: [cloneObj(obj)] }] });
    }
  }

  private dropMissingSelection(): void {
    for (const id of [...this.selection]) if (!this.doc.get(id)) this.selection.delete(id);
  }

  private changed(): void {
    this.hooks.onChange?.();
  }
}
